<template>
  <div
    class="base-input"
    ref="data"
    :class="{
      readonly,
      loading,
      error: hasValidationError,
      'date-input': !withTime,
      'date-time-input': withTime,
    }"
    :style="{ width: width }"
    @focusout="blurHandler"
    tabindex="-1"
  >
    <label
      v-if="label && readonly"
      :class="['input__label', { readonly }]"
    >
      {{ label }}
    </label>

    <div class="input-content"
      @click.stop="toggleModal"
    >
      <input
        :value="inputValue"
        autocomplete="off"
        ref="input"
        :name="name"
        @focus="$emit('focus')"
        :placeholder="readonly ? '-' : placeholder"
        class="input__field"
        :readonly="readonly"
        :disabled="disabled || loading"
        @input="handleInput"
      />

      <svgicon
        :icon="onlyTime ? 'arrow' : 'calendar'"
        :width="onlyTime ? '15px' : '24px'"
        class="icon"
        v-if="!readonly && !disabled"
      />
    </div>

    <Calendar
      v-if="dateModal"
      @close="(dateModal = false), validate()"
      :withTime="withTime"
      :value="inputValue"
      @setValue="setDateModalValue"
      :right="right"
    />

    <Time
      v-if="timeModal"
      @close="(timeModal = false), validate()"
      :value="inputValue"
      @setValue="setDateModalValue"
    />

    <slot v-if="hasValidationError && !serverValidationPending" />
  </div>
</template>

<script>
import Base from "./Base/Base.vue";
import moment from "moment";
import masker from "@titou10/v-mask/dist/masker";
import Calendar from "@/components/Form/Vinput/Calendar.vue";
import Time from "@/components/Form/Vinput/Time.vue";

const DATE_FORMAT = "DD.MM.YYYY";
const DATETIME_FORMAT = "DD.MM.YYYY HH:mm";
const ONLY_TIME = "HH:mm";

// Has following validations: isValidDate, minDate, maxDate
export default {
  name: "Date",
  extends: Base,
  components: {
    Calendar,
    Time
  },
  props: {
    minDate: {
      type: [String, Date],
      default: null,
    },
    maxDate: {
      type: [String, Date],
      default: null,
    },
    withTime: {
      type: Boolean,
      default: false,
    },
    onlyTime: {
      type: Boolean,
      default: false,
    },
    right: {
      type: Boolean,
      default: false,
    }
  },
  data() {
    return {
      usedMask: {
        mask: this.onlyTime
          ? "##:##"
          : this.withTime
          ? "##.##.#### ##:##"
          : "##.##.####",
        nullIfEmpty: false,
      },
      lang: {
        formatLocale: {
          monthsShort: [
            "Янв",
            "Фев",
            "Мар",
            "Апр",
            "Май",
            "Июн",
            "Июл",
            "Авг",
            "Сен",
            "Окт",
            "Ноя",
            "Дек",
          ],
          weekdaysMin: ["Вс", "Пн", "Вт", "Ср", "Чт", "Пт", "Сб"],
          firstDayOfWeek: 1,
        },
      },
      dateModal: false,
      timeModal: false,
    };
  },
  validations() {
    return {
      inputValue: this.dateRules,
    };
  },
  methods: {
    handleInput({ target }) {
      target.value = masker(target.value, this.usedMask.mask);
      this.inputValue = target.value;
    },
    handlerBlur({ target }) {
      target.value = masker(target.value, this.usedMask.mask);
      this.inputValue = target.value;
      this.$emit("blur");
      this.validate();
    },
    blurHandler(event) {
      const enteringParent = this.$refs.data.contains(event.relatedTarget);
      if (!enteringParent) {
        this.dateModal = false
        this.timeModal = false
      }
    },
    focus() {
      this.$refs.input.focus();
    },
    toggleModal() {
      if (!this.disabled) {
        this.$v.inputValue.$reset();
        this.$nextTick(() => this.$refs.input?.focus())
        if(!this.onlyTime) {
          this.dateModal = !this.dateModal
        } else {
          this.timeModal = !this.timeModal
          if (this.timeModal && this.inputValue === "") this.inputValue = "00:00"
        }
      }
    },
    setDateModalValue(val) {
      this.inputValue = val;
    }
  },
  computed: {
    dateFormat() {
      return this.onlyTime
        ? ONLY_TIME
        : this.withTime
        ? DATETIME_FORMAT
        : DATE_FORMAT;
    },
    dateRules() {
      const validators = { ...this.rules };

      validators.isValidDate = (date) => {
        if (date) {
          const newDate = moment(date, this.dateFormat);

          if (!this.onlyTime) {
            let dateSplit;
            if (this.withTime) {
              const dateParts = date.split(" ");
              dateSplit = dateParts[0].split(".");
            } else {
              dateSplit = date.split(".");
            }

            if (dateSplit.length > 2) {
              const year = +dateSplit.pop();

              return !isNaN(newDate) && year > 1900 && year < 2100;
            } else return !date.length;
          } else {
            let timeSplit = date.split(":");

            return (
              timeSplit.length === 2 &&
              +timeSplit[0] < 24 &&
              timeSplit[1] &&
              +timeSplit[1] < 60
            );
          }
        } else return true;
      };

      if (this.minDate) {
        validators.minDate = (date) => {
          if (!date) return true;

          let minDate = this.minDate;
          if (this.minDate instanceof Date) {
            minDate = moment(this.minDate).format(this.dateFormat);
          }

          return (
            moment(date, this.dateFormat) >= moment(minDate, this.dateFormat)
          );
        };
      }

      if (this.maxDate) {
        validators.maxDate = (date) =>
          moment(date, this.dateFormat) < moment(this.maxDate, this.dateFormat);
      }

      return validators;
    },
    stringToDate() {
      const parsed = moment(this.inputValue, this.dateFormat).toDate();

      if (isNaN(parsed)) {
        return this.today;
      } else {
        return parsed;
      }
    },
    today() {
      return new Date();
    },
  },
};
</script>

<style lang="scss" scoped>
.date-input {
  display: flex;
  flex-direction: column;
}

.input-content {
  display: flex;
  align-items: center;
  position: relative;
  width: 100%;

  input {
    width: 100%;
  }
}

.icon {
  max-width: 24px;
  max-height: 24px;
  position: absolute;
  transition: all 0.2s;
  background: transparent;
  fill: gray;
  right: 13px;
  cursor: pointer;
  fill: $dark-blue;

  &:active {
    fill: $blue;
  }
}
</style>
